import React, { useEffect, useState, useMemo } from "react";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, PlusCircle, Calendar as CalendarIcon, CreditCard, DollarSign, CheckCircle, Clock, XCircle, TrendingUp, AlertTriangle, Printer, Download, ChevronDown, Check, User, ShieldCheck, BadgeCheck, Info, ArrowRight, Phone, Eye, Banknote, HandCoins, BadgeAlert } from "lucide-react";
import { motion } from "framer-motion";
import { useQuery, useQueryClient } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useConfirmModal from "@/hooks/useConfirmModal";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useAuth } from "@/context/auth.context";
import LoanApprovalModal from "@/components/LoanApprovalModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { StatusBadge, ApprovalProgressBadge } from "@/components/ui/StatusBadge";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MemberLoanProfile from "@/components/loans/MemberLoanProfile";
import { Card, CardContent } from "@/components/ui/card";

// ─── Status helpers ──────────────────────────────────────────────────────────
const normalizeLoanStatus = (status: string | null | undefined) =>
  String(status || "").toLowerCase().replace(/\s+/g, "_");

const APPROVABLE_STATUSES = ["pending", "pending_approval", "awaiting_approval", "approved_by_leaders"];

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  pending_approval: "Pending Approval",
  awaiting_approval: "Awaiting Approval",
  approved_by_leaders: "Approved by Leaders",
  approved: "Approved",
  ready_for_disbursement: "Ready for Disbursement",
  disbursed: "Disbursed",
  active: "Active",
  overdue: "Overdue",
  completed: "Completed",
  fully_repaid: "Fully Repaid",
  rejected: "Rejected",
  defaulted: "Defaulted",
  cancelled: "Cancelled",
  draft: "Draft",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  pending_approval: "bg-amber-100 text-amber-700 border-amber-200",
  awaiting_approval: "bg-amber-100 text-amber-700 border-amber-200",
  approved_by_leaders: "bg-blue-100 text-blue-700 border-blue-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  ready_for_disbursement: "bg-blue-100 text-blue-700 border-blue-200",
  disbursed: "bg-blue-600 text-white border-none",
  active: "bg-blue-600 text-white border-none",
  overdue: "bg-red-100 text-red-700 border-red-200",
  completed: "bg-emerald-600 text-white border-none",
  fully_repaid: "bg-emerald-600 text-white border-none",
  rejected: "bg-red-100 text-red-700 border-red-200",
  defaulted: "bg-red-600 text-white border-none",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  draft: "bg-gray-100 text-gray-600 border-gray-200",
};

// Loan form schema
const loanFormSchema = z.object({
  teacherId: z.number().min(1, "Member is required"),
  amount: z.number().min(1, "Amount must be greater than 0"),
  interestRate: z.number().min(0).max(100, "Interest rate must be between 0 and 100"),
  termMonths: z.number().min(1, "Term must be at least 1 month"),
  purpose: z.string().min(1, "Purpose is required"),
  requestDate: z.date(),
  loanTypeId: z.number().optional().nullable(),
});

type LoanFormData = z.infer<typeof loanFormSchema>;

export default function Loans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "requestDate",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const confirmModal = useConfirmModal();
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedLoanForApproval, setSelectedLoanForApproval] = useState<any>(null);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [loanToApprove, setLoanToApprove] = useState<any>(null);
  const [memberProfileOpen, setMemberProfileOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | string | null>(null);

  // Fetch loans data
  const recordsQuery = useQuery(
    ["loans", pageIndex, pageSize, searchText, columnFilters, sorting],
    async () => {
      const params = new URLSearchParams({
        page: (pageIndex + 1).toString(),
        limit: pageSize.toString(),
        search: searchText,
      });

      const { data } = await api.get(`/loans?${params}`);
      return data;
    },
    {
      keepPreviousData: true,
    }
  );

  const loanTableData = recordsQuery.data?.data || [];
  const totalLoanRecords = recordsQuery.data?.count || loanTableData.length;

  // Compute dashboard metrics from all visible data
  const totalAmountDisbursed = loanTableData
    .filter((l: any) => ["disbursed", "active", "completed", "defaulted", "fully_repaid"].includes(normalizeLoanStatus(l?.status)))
    .reduce((sum: number, l: any) => sum + Number(l?.amount || 0), 0);

  const totalRepaid = loanTableData.reduce(
    (sum: number, l: any) => sum + Number(l?.totalPaid || 0), 0
  );

  const outstandingBalance = loanTableData.reduce(
    (sum: number, l: any) => sum + Number(l?.dueAmount || 0), 0
  );

  const expectedInterestProfit = loanTableData
    .filter((l: any) => ["disbursed", "active", "approved", "completed", "pending", "pending_approval", "approved_by_leaders"].includes(normalizeLoanStatus(l?.status)))
    .reduce((sum: number, l: any) => {
      const upfront = Number(l?.upfrontInterest || 0);
      if (upfront > 0) return sum + upfront;
      const principal = Number(l?.amount || 0);
      const rate = Number(l?.interestRate || 0);
      const term = Number(l?.termMonths || 0);
      const flatInterest = principal * (rate / 100) * (term / 12);
      return sum + Math.max(0, flatInterest);
    }, 0);

  const overdueLoans = loanTableData.filter((l: any) => normalizeLoanStatus(l?.status) === "defaulted").length;
  const pendingLoans = loanTableData.filter((l: any) => APPROVABLE_STATUSES.includes(normalizeLoanStatus(l?.status))).length;
  const approvedCount = loanTableData.filter((l: any) => normalizeLoanStatus(l?.status) === "approved").length;

  const formatCurrency = (value: number) => `${value.toLocaleString()} FRW`;

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "loanCode",
      id: "loanCode",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Loan ID" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-sm font-semibold text-gray-900">{row.original.loanCode || `LN-${String(row.original.id).padStart(4, "0")}`}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "memberName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member" />
      ),
      cell: ({ row }) => {
        const rawCode = row.original.memberCode || row.original.teacherCode;
        const memberCode = rawCode && !String(rawCode).startsWith("TMP-") ? rawCode : null;
        const name = row.original.memberName || row.original.teacherName || "Unknown";
        const phone = row.original.teacherPhone || row.original.memberPhone;
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                {name?.split(" ").map((w: string) => w?.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <button
                className="font-semibold text-sm text-[#009A44] hover:underline cursor-pointer transition-colors"
                onClick={() => {
                  navigate(`/loans/${row.original.loanCode || row.original.id}`);
                }}
              >
                {name}
              </button>
              <div className="flex items-center gap-2">
                {memberCode ? (
                  <span className="text-xs text-gray-500">ID: {memberCode}</span>
                ) : (
                  <span className="text-xs text-gray-400 italic">No ID</span>
                )}
                {phone && (
                  <span className="text-xs text-gray-400 flex items-center gap-0.5">
                    <Phone className="h-2.5 w-2.5" /> {phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Amount" />
      ),
      cell: ({ row }) => {
        const amount = row.getValue("amount") as number;
        return (
          <div className="flex items-center space-x-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="font-medium">{amount?.toLocaleString()} FRW</span>
          </div>
        );
      },
    },
    {
      accessorKey: "dueAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Remaining" />
      ),
      cell: ({ row }) => {
        const loan = row.original;
        const dueAmount = Number(loan.dueAmount || 0);
        const totalPaid = Number(loan.totalPaid || 0);
        return (
          <span className={cn("text-sm font-medium", dueAmount > 0 ? "text-amber-700" : "text-green-700")}>
            {formatCurrency(dueAmount)}
          </span>
        );
      },
    },
    {
      accessorKey: "monthlyPayment",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Monthly" />
      ),
      cell: ({ row }) => {
        const loan = row.original;
        return (
          <span className="text-sm">{formatCurrency(loan.monthlyPayment || 0)}</span>
        );
      },
    },
    {
      accessorKey: "interestRate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Interest Rate" />
      ),
      cell: ({ row }) => {
        const rate = row.getValue("interestRate") as number;
        return (
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4 text-[#009A44]" />
            <span>{rate}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: "termMonths",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Term" />
      ),
      cell: ({ row }) => {
        const term = row.getValue("termMonths") as number;
        return (
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-[#009A44]" />
            <span>{term} months</span>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const rawStatus = row.getValue("status") as string;
        return <StatusBadge status={rawStatus || "pending"} />;
      },
    },
    {
      id: "approvalProgress",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Approval" />
      ),
      cell: ({ row }) => {
        const loan = row.original;
        const status = normalizeLoanStatus(loan.status);
        const count = loan.approvalCount || 0;
        if (status === "rejected") return <StatusBadge status="rejected" size="sm" />;
        if (status === "approved" || count >= 3) return <StatusBadge status="approved" size="sm" />;
        if (status === "completed" || status === "fully_repaid" || status === "disbursed" || status === "active") {
          return <StatusBadge status={status} size="sm" />;
        }
        if (APPROVABLE_STATUSES.includes(status)) return <ApprovalProgressBadge approved={count} total={3} />;
        return <StatusBadge status={status || "pending"} size="sm" />;
      },
      enableSorting: false,
    },
    {
      accessorKey: "requestDate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Request Date" />
      ),
      cell: ({ row }) => {
        const date = row.getValue("requestDate") as string;
        return (
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-[#009A44]" />
            <span>{date ? format(new Date(date), "MMM dd, yyyy") : <StatusBadge status="awaiting_submission" size="sm" />}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "purpose",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Purpose" />
      ),
      cell: ({ row }) => {
        const purpose = row.getValue("purpose") as string;
        return (
          <div className="max-w-[200px] truncate">
            {purpose || "No purpose specified"}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const loan = row.original;
        const status = normalizeLoanStatus(loan.status);
        const count = loan.approvalCount || 0;
        const alreadyApproved = loan.alreadyApprovedByCurrentUser;
        const isApprovable = APPROVABLE_STATUSES.includes(status) && (count < 3 || user?.isAdmin);
        const isFullyApproved = count >= 3;
        const canDisburse = isFullyApproved && ["approved", "pending", "pending_approval", "approved_by_leaders"].includes(status);

        return (
          <div className="flex items-center gap-1.5">
            {/* Always show View */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                navigate(`/loans/${loan.loanCode || loan.id}`);
              }}
            >
              View
            </Button>

            {/* Show Approve when approvable and current leader hasn't approved (admin always sees it) */}
            {isApprovable && (!alreadyApproved || user?.isAdmin) && (
              <Button
                size="sm"
                className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  setSelectedLoanForApproval(loan);
                  setApprovalModalOpen(true);
                }}
              >
                Approve
              </Button>
            )}

            {/* Show Approved badge when current leader already approved */}
            {isApprovable && alreadyApproved && (
              <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700 border-green-200">
                <CheckCircle className="h-3 w-3 mr-1" />
                Approved
              </Badge>
            )}

            {/* Show Fully Approved badge when all 3 approved but not yet disbursed */}
            {isFullyApproved && canDisburse && (
              <Badge className="text-[10px] bg-green-600 text-white">
                <CheckCircle className="h-3 w-3 mr-1" />
                Fully Approved
              </Badge>
            )}

            {/* Show Disburse button after 3/3 approval */}
            {canDisburse && (
              <Button
                size="sm"
                className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                onClick={async () => {
                  try {
                    await api.patch(`/loans/${loan.id}/disburse`);
                    toast.success("Loan disbursed successfully");
                    recordsQuery.refetch();
                  } catch (error) {
                    toast.error("Failed to disburse loan");
                  }
                }}
              >
                Disburse
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-7 w-7 p-0">
                  <span className="sr-only">More</span>
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    confirmModal.open({
                      meta: {
                        id: loan.id,
                        name: `${loan.memberName || loan.teacherName} - ${loan.amount} FRW`,
                      }
                    });
                  }}
                  className="text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const handleDelete = async (meta) => {
    confirmModal.setIsLoading(true);
    try {
      await api.delete(`/loans/${meta.id}`);
      toast.success("Loan deleted successfully");
      recordsQuery.refetch();
      confirmModal.close();
    } catch (error: any) {
      console.error("Delete loan error:", error);
      confirmModal.setIsLoading(false);
      const errorMessage = error.response?.data?.message || error.message || "Failed to delete loan";
      toast.error(errorMessage);
    }
  };

  // Status options for filtering
  const statusOptions = [
    { label: "Pending Approval", value: "pending_approval" },
    { label: "Approved by Leaders", value: "approved_by_leaders" },
    { label: "Approved", value: "approved" },
    { label: "Disbursed", value: "disbursed" },
    { label: "Active", value: "active" },
    { label: "Overdue", value: "overdue" },
    { label: "Completed", value: "completed" },
    { label: "Fully Repaid", value: "fully_repaid" },
    { label: "Rejected", value: "rejected" },
    { label: "Defaulted", value: "defaulted" },
  ];

  // Term options for filtering
  const termOptions = [
    { label: "1-3 months", value: "1-3" },
    { label: "4-6 months", value: "4-6" },
    { label: "7-12 months", value: "7-12" },
    { label: "13+ months", value: "13+" }
  ];

  // Summary card metrics
  const activeLoans = loanTableData.filter((l: any) => ["active", "disbursed"].includes(normalizeLoanStatus(l?.status))).length;
  const completedLoans = loanTableData.filter((l: any) => ["completed", "fully_repaid"].includes(normalizeLoanStatus(l?.status))).length;

  const summaryCards = [
    { label: "Total Loans", value: String(totalLoanRecords), icon: CreditCard, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Disbursed", value: formatCurrency(totalAmountDisbursed), icon: Banknote, color: "text-green-600", bg: "bg-green-50" },
    { label: "Repaid", value: formatCurrency(totalRepaid), icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Outstanding", value: formatCurrency(outstandingBalance), icon: HandCoins, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Expected Interest", value: formatCurrency(expectedInterestProfit), icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50" },
    { label: "Pending", value: String(pendingLoans), icon: Clock, color: "text-orange-600", bg: "bg-orange-50" },
    { label: "Active", value: String(activeLoans), icon: BadgeCheck, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Overdue", value: String(overdueLoans), icon: BadgeAlert, color: "text-red-600", bg: "bg-red-50" },
  ];

  // Interest rate options for filtering
  const interestRateOptions = [
    { label: "0-3%", value: "0-3" },
    { label: "4-6%", value: "4-6" },
    { label: "7-10%", value: "7-10" },
    { label: "11%+", value: "11+" }
  ];

  const facets = [
    { name: "status", title: "Status", options: statusOptions },
    { name: "termMonths", title: "Term", options: termOptions },
    { name: "interestRate", title: "Interest Rate", options: interestRateOptions },
  ];

  return (
    <>
      <ConfirmModal
        title={"Are you sure you want to delete this loan?"}
        description={`This will permanently delete ${confirmModal.meta?.name} and cannot be undone.`}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      {/* Loan Approval Modal */}
      {selectedLoanForApproval && (
        <LoanApprovalModal
          isOpen={approvalModalOpen}
          setIsOpen={setApprovalModalOpen}
          loanId={selectedLoanForApproval.id}
        />
      )}

      {/* Member Loan Profile Dialog */}
      <Dialog open={memberProfileOpen} onOpenChange={setMemberProfileOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Member Loan Profile</DialogTitle>
          </DialogHeader>
          {selectedMemberId && (
            <MemberLoanProfile
              memberId={selectedMemberId}
              onClose={() => setMemberProfileOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="sm:px-4 px-3 pt-3">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Loans</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and review all loan applications</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {summaryCards.slice(0, 5).map((card, i) => (
            <Card
              key={card.label}
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow",
                card.bg
              )}
              onClick={() => {
                if (card.label === "Pending") setColumnFilters([{ id: "status", value: ["pending", "pending_approval", "awaiting_approval", "approved_by_leaders"] }]);
                else if (card.label === "Active") setColumnFilters([{ id: "status", value: ["active", "disbursed"] }]);
                else if (card.label === "Overdue") setColumnFilters([{ id: "status", value: ["overdue", "defaulted"] }]);
                else setColumnFilters([]);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">{card.label}</span>
                  <card.icon className={cn("h-5 w-5", card.color)} />
                </div>
                <p className={cn("text-xl font-bold", card.color)}>
                  {card.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Second row: smaller cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {summaryCards.slice(5).map((card, i) => (
            <Card
              key={card.label}
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow",
                card.bg
              )}
              onClick={() => {
                if (card.label === "Pending") setColumnFilters([{ id: "status", value: ["pending", "pending_approval", "awaiting_approval", "approved_by_leaders"] }]);
                else if (card.label === "Active") setColumnFilters([{ id: "status", value: ["active", "disbursed"] }]);
                else if (card.label === "Overdue") setColumnFilters([{ id: "status", value: ["overdue", "defaulted"] }]);
                else setColumnFilters([]);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">{card.label}</span>
                  <card.icon className={cn("h-5 w-5", card.color)} />
                </div>
                <p className={cn("text-xl font-bold", card.color)}>
                  {card.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {recordsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : recordsQuery.error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error loading loans</p>
            <p className="text-sm mt-1">
              {(() => {
                const err = recordsQuery.error as any;
                return err?.response?.data?.message || err?.response?.data?.error || err?.message || "Unknown error";
              })()}
            </p>
          </div>
        ) : (
          <DataTable
            data={loanTableData}
            columns={columns}
            isLoading={recordsQuery.isLoading}
            isFetching={recordsQuery.isFetching}
            onSearch={setSearchText}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            setSorting={setSorting}
            sorting={sorting}
            setPagination={setPagination}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={Math.ceil(totalLoanRecords / pageSize)}
            facets={facets}
            defaultColumnVisibility={{}}
            Action={() => (
              <Button
                size="sm"
                className="bg-[#009A44] hover:bg-[#008238] text-white h-8 text-xs"
                onClick={() => navigate('/loans/apply')}
              >
                <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                New Loan
              </Button>
            )}
          />
        )}
      </div>
    </>
  );
}